import { useEffect, useRef, useState } from "react";

const DEFAULT_DURATION_MS = 500;

function easeOutExpo(t: number): number {
  if (t >= 1) {
    return 1;
  }
  return 1 - 2 ** (-10 * t);
}

export function formatUsdWithSpaceAfterSymbol(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    currency: "USD",
    style: "currency"
  })
    .formatToParts(amount)
    .map((part) => (part.type === "currency" ? `${part.value} ` : part.value))
    .join("");
}

export function useAnimatedUsdString(
  value: number,
  durationMs: number = DEFAULT_DURATION_MS
): string {
  const rafRef = useRef(0);
  const displayRef = useRef(0);
  const [label, setLabel] = useState(() => formatUsdWithSpaceAfterSymbol(0));

  useEffect(() => {
    const from = displayRef.current;
    const start = performance.now();

    const step = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = easeOutExpo(t);
      const fromCents = Math.round(from * 100);
      const toCents = Math.round(value * 100);
      const curCents = Math.round(fromCents + (toCents - fromCents) * eased);
      const cur = curCents / 100;
      displayRef.current = cur;
      setLabel(formatUsdWithSpaceAfterSymbol(cur));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, durationMs]);

  return label;
}
